import * as React from "react";
import { useEffect } from "react";
import { TailSpin } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  useCreateUserMutation,
  useUpdateUserOrgStatusMutation,
} from "state/rtkQuery/user/userMutations";

import { useGetUserByEmailQuery } from "state/rtkQuery/user/userQueries";

import BasicAlerts from "components/BasicAlert/BasicAlert";
import { extractApiErrorMessage } from "utils/accessManagement/extractApiErrorMessage";
import { showToast } from "utils/reusableToast";

import TOS from "./TOS";

import "./modalTOS.css";

function TOSModal(props) {
  // Local states
  const [open, setOpen] = React.useState(false);
  const [scroll, setScroll] = React.useState("paper");
  const [checked, setChecked] = React.useState(false);

  // Checkbox Oncheck function
  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  // Fetch user by email only (all platforms) to determine if user already exists anywhere
  const { data: allPlatformUserData } = useGetUserByEmailQuery(
    { email: props.email },
    { skip: !props.email }
  );

  // RTK Query for updating user entry
  const [
    updateUser,
    {
      data: updatedUserData,
      isLoading: isUpdatingUser,
      isSuccess: isUpdatingUserSuccess,
      error: userUpdateError,
    },
  ] = useUpdateUserOrgStatusMutation();

  // RTK Query for creating new user entry
  const [
    createUser,
    {
      data: createdUserData,
      isLoading: isCreatingUser,
      isSuccess: isCreatingUserSuccess,
      error: createUserError,
    },
  ] = useCreateUserMutation();

  // Function to check if user record exists or not. If user exists on any platform, patch tos_accepted, else create new user
  const handleNewClose = async () => {
    if (allPlatformUserData?.length > 0) {
      // User exists (on this or another platform) - patch tos_accepted
      const body = {
        platforms: {
          [props.platformId]: {
            tos_accepted: true,
            user_name:
              allPlatformUserData[0]?.platforms?.[props.platformId]?.user_name,
          },
        },
      };

      updateUser({
        userId: allPlatformUserData[0]?._id,
        body: body,
        attribute: "tos_accepted",
      });
    } else {
      // User doesn't exist anywhere - create new user record
      const userObj = {
        email: props.email.toLowerCase(),
        platforms: {
          [props.platformId]: {
            tos_accepted: true,
            name: props.platformName,
          },
        },
      };

      const res = await createUser(userObj);
      // The global RTK Query error middleware suppresses toasts for 404s
      // (most 404s elsewhere just mean "not found yet"), but a 404 here
      // means AD rejected this email (e.g. "User X does not exist in AD") —
      // a real failure that would otherwise leave the modal open with no
      // feedback and a stuck "Accept" button. Handle it locally instead.
      if (res.error) {
        showToast({
          title: "Couldn't accept terms",
          description: extractApiErrorMessage(res.error, "Failed to create your account. Please try again."),
          type: "error",
          id: "tos-modal-create-user-error",
        });
      }
    }
  };

  // Close modal on successful TOS submission
  useEffect(() => {
    if (isUpdatingUserSuccess || isCreatingUserSuccess) {
      // Trigger callback if provided (for new users to show platform selection)
      if (props.onTosAcceptedNewUser) {
        props.onTosAcceptedNewUser();
      }
      // Always close the TOS modal
      props.close();
    }
  }, [isUpdatingUserSuccess, isCreatingUserSuccess]);

  // UseState to set if bottom Scroll is reached
  const [bottom, setBottom] = React.useState(true);

  // Function To Enable Checkbox after Scroll Ends
const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - (e.target.scrollTop + e.target.clientHeight);

    // Use a threshold to allow for zoom and rounding errors
    if (Math.abs(bottom) <= 2) {
      setBottom(false);
    } else {
      setBottom(true);
    }
  };

  const handleBackdropClick = (event) => {
    // these fail to keep the modal open
    event.stopPropagation();
    return false;
  };

  const descriptionElementRef = React.useRef(null);

  return (
    <div>
      {/* Dialog Content */}
      <Dialog
        open={props.show}
        onBackdropClick={handleBackdropClick}
        disableEscapeKeyDown={true}
        onClose={props.close}
        scroll={scroll}
        sx={{
          backdropFilter: "blur(5px)",
          //other styles here
        }}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        {/* Dialog Title */}
        <DialogTitle id="scroll-dialog-title" sx={{ background: "white" }}>
          DT Global Enterprise Code Repository (ECR) Rules of the Road{" "}
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent
          dividers={scroll === "paper"}
          onScroll={handleScroll}
          sx={{
            maxHeight: "calc(100% - 209px)",
            maxWidth: "600px",
            color: "black",
            letterSpacing: "0.00938em",
          }}
        >
          {/* TOS Text Content */}
          <TOS />
        </DialogContent>
        {/* Checkbox and Button */}
        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Stack
            sx={{
              padding: "4px 16px",
              width: "100%",
              justifyContent: "space-between",
            }}
            direction="row"
            spacing={10}
          >
            <FormControlLabel
              value="end"
              disabled={bottom}
              control={
                <Checkbox
                  checked={checked}
                  onChange={handleChange}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label="I have read and agree to the Deloitte GitHub EMU Terms of Service"
              labelPlacement="end"
            />

            <Stack direction="row" spacing={2}>
              {/* Lets the user back out of accepting TOS for this platform and
                  return to the dashboard instead of being stuck in the modal. */}
              {props.onCancel ? (
                <Button
                  sx={{
                    backgroundColor: "#008080",
                    letterSpacing: "0.5px",
                    padding: "0px 16px",
                    borderRadius: "2px",
                    fontSize: "14px",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#006161",
                      color: "white",
                    },
                  }}
                  onClick={props.onCancel}
                  disabled={isCreatingUser || isUpdatingUser}
                >
                  Back
                </Button>
              ) : null}

              <Button
                sx={{
                  backgroundColor: `${!checked ? "#e9e9e9" : "#008080"}`,
                  letterSpacing: "0.5px",
                  padding: "0px 16px",
                  borderRadius: "2px",
                  fontSize: "14px",
                  color: `${!checked ? "gray" : "white"}`,
                  "&:hover": {
                    backgroundColor: "#006161",
                    color: "white",
                  },
                }}
                onClick={handleNewClose}
                disabled={!checked}
              >
                {/* Loader on submit */}
                {isCreatingUser || isUpdatingUser ? (
                  <TailSpin
                    color="hsl(81.43deg 69.37% 43.53%)"
                    height={25}
                    width={25}
                  />
                ) : (
                  "Accept"
                )}
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default TOSModal;
